import type {
  ConstructionGroup,
  ConstructionLine,
  Decoration,
  Hardware,
  Hole,
  Cutout,
  Piece,
  Project,
  StitchLine,
  TopLevelConstructionRef,
  UserStamp,
} from "./Project";
import {
  groupAncestorIds,
  nextConstructionGroupName,
  nextConstructionOffsetName,
  pieceHardware,
  pieceStitchLines,
  projectConstructionGroups,
  projectConstructionLines,
  projectTracingImages,
  topLevelConstructionOrder,
} from "./Project";
import {
  descendantLeafIds,
  groupRef,
  imageRef,
  lineRef,
  objectRefAncestorIds,
} from "./ConstructionObject";
import { newId } from "./ids";

export function withPiece(p: Project, piece: Piece): Project {
  const idx = p.pieces.findIndex((pc) => pc.id === piece.id);
  const pieces =
    idx >= 0
      ? p.pieces.map((pc) => (pc.id === piece.id ? piece : pc))
      : [...p.pieces, piece];
  return { ...p, pieces };
}

export function withoutPiece(p: Project, pieceId: string): Project {
  return { ...p, pieces: p.pieces.filter((pc) => pc.id !== pieceId) };
}

/** Reorder leather parts by their LIST position (#678). The parts list is shown
 *  top-to-bottom in REVERSE of `project.pieces` (a higher row draws on top), and
 *  new parts append to the array so they land at the top of the list. This moves
 *  `movedIds` so they sit immediately BEFORE `beforeId` in that list, or at the
 *  BOTTOM of the list when `beforeId` is null, preserving their relative order.
 *  Returns the project unchanged (same ref) when nothing actually moves. */
export function movePiecesInList(
  p: Project,
  movedIds: string[],
  beforeId: string | null,
): Project {
  const moved = new Set(movedIds);
  if (moved.size === 0) return p;
  const list = [...p.pieces].reverse(); // top-to-bottom display order
  const movedInList = list.filter((pc) => moved.has(pc.id));
  if (movedInList.length === 0) return p;
  const kept = list.filter((pc) => !moved.has(pc.id));
  let idx =
    beforeId == null ? kept.length : kept.findIndex((pc) => pc.id === beforeId);
  if (idx < 0) idx = kept.length;
  const pieces = [
    ...kept.slice(0, idx),
    ...movedInList,
    ...kept.slice(idx),
  ].reverse();
  // No-op guard — same order → keep the original reference.
  const unchanged =
    pieces.length === p.pieces.length &&
    pieces.every((pc, i) => pc === p.pieces[i]);
  if (unchanged) return p;
  return { ...p, pieces };
}

export type ReorderStepDir = "up" | "down";

/** Move every selected element one slot toward the front ("up") or the back
 *  ("down") of `arr`, PER ITEM: a run of selected neighbours steps as a
 *  block, a non-contiguous selection keeps its gaps, and items already
 *  pressed against the end (or against another selected item there) stay.
 *  Returns the input array reference when nothing moves. */
function stepArray<T>(
  arr: readonly T[],
  selected: (t: T) => boolean,
  dir: ReorderStepDir,
): readonly T[] {
  const out = [...arr];
  let moved = false;
  if (dir === "up") {
    for (let i = 1; i < out.length; i++) {
      if (selected(out[i]!) && !selected(out[i - 1]!)) {
        [out[i - 1], out[i]] = [out[i]!, out[i - 1]!];
        moved = true;
      }
    }
  } else {
    for (let i = out.length - 2; i >= 0; i--) {
      if (selected(out[i]!) && !selected(out[i + 1]!)) {
        [out[i + 1], out[i]] = [out[i]!, out[i + 1]!];
        moved = true;
      }
    }
  }
  return moved ? out : arr;
}

/** Keyboard reorder for leather parts (#759): move the selected parts one
 *  ROW up ("up" = toward the top of the layers list = drawn on top) or down,
 *  mirroring what a one-slot drag-reorder (#678) would do. Per-item
 *  stepping — see {@link stepArray}. Returns the same reference when nothing
 *  moves (already at the edge). */
export function stepPiecesInList(
  p: Project,
  ids: readonly string[],
  dir: ReorderStepDir,
): Project {
  const set = new Set(ids);
  if (set.size === 0) return p;
  const list = [...p.pieces].reverse(); // top-to-bottom display order
  const stepped = stepArray(list, (pc) => set.has(pc.id), dir);
  if (stepped === list) return p;
  return { ...p, pieces: [...stepped].reverse() };
}

/** Keyboard reorder for construction objects (#759): move the selected
 *  lines / groups / tracing images one row up or down among their SIBLINGS,
 *  mirroring the panel's drag-reorder strips. Top-level items step through
 *  the interleaved groups + loose lines sequence (#887, persisted via
 *  `constructionTopLevelOrder`); grouped items step within their group's
 *  per-kind display arrays (`groupIds` / `lineIds` / `imageIds` — the model
 *  keeps no interleaved order inside a group, so neither does the step).
 *  Selections spanning several parents step independently in each. Returns
 *  the same reference when nothing moves. */
export function stepConstructionItems(
  p: Project,
  items: {
    lineIds?: readonly string[];
    groupIds?: readonly string[];
    imageIds?: readonly string[];
  },
  dir: ReorderStepDir,
): Project {
  const lineSet = new Set(items.lineIds ?? []);
  const groupSet = new Set(items.groupIds ?? []);
  const imageSet = new Set(items.imageIds ?? []);
  if (lineSet.size === 0 && groupSet.size === 0 && imageSet.size === 0) {
    return p;
  }
  let next = p;
  // Top level: the interleaved groups + loose lines sequence. Grouped lines
  // never appear here (the resolver drops them), so a grouped selection
  // can't leak into a top-level move.
  const top = topLevelConstructionOrder(p);
  const steppedTop = stepArray(
    top,
    (ref) =>
      ref.kind === "group" ? groupSet.has(ref.id) : lineSet.has(ref.id),
    dir,
  );
  if (steppedTop !== top) {
    next = { ...next, constructionTopLevelOrder: [...steppedTop] };
  }
  // Within groups: each kind keeps its own display array. A fully-selected
  // group's members (auto-promotion selects both) are all-blocked against
  // each other, so the inner pass no-ops and only the group's own ref moves.
  let groupsChanged = false;
  const nextGroups = projectConstructionGroups(next).map((g) => {
    const lineIds = stepArray(g.lineIds, (id) => lineSet.has(id), dir);
    const groupIds = g.groupIds
      ? stepArray(g.groupIds, (id) => groupSet.has(id), dir)
      : g.groupIds;
    const imageIds = g.imageIds
      ? stepArray(g.imageIds, (id) => imageSet.has(id), dir)
      : g.imageIds;
    if (
      lineIds === g.lineIds &&
      groupIds === g.groupIds &&
      imageIds === g.imageIds
    ) {
      return g;
    }
    groupsChanged = true;
    return {
      ...g,
      lineIds: [...lineIds],
      ...(groupIds ? { groupIds: [...groupIds] } : {}),
      ...(imageIds ? { imageIds: [...imageIds] } : {}),
    };
  });
  if (groupsChanged) next = { ...next, constructionGroups: nextGroups };
  return next;
}

export function withHole(p: Project, pieceId: string, hole: Hole): Project {
  return updatePiece(p, pieceId, (piece) => ({
    ...piece,
    holes: [...piece.holes, hole],
  }));
}

export function withCutout(
  p: Project,
  pieceId: string,
  cutout: Cutout,
): Project {
  return updatePiece(p, pieceId, (piece) => ({
    ...piece,
    cutouts: [...piece.cutouts, cutout],
  }));
}

/** Add or replace a hardware entry on a piece. Replacement key is the
 *  `holeId` (one hardware item per hole — re-clicking a hole with new
 *  type/colour/size overwrites the prior entry rather than stacking). */
export function withHardware(
  p: Project,
  pieceId: string,
  hardware: Hardware,
): Project {
  return updatePiece(p, pieceId, (piece) => {
    const existing = pieceHardware(piece);
    const idx = existing.findIndex((h) => h.holeId === hardware.holeId);
    const next =
      idx >= 0
        ? existing.map((h) => (h.holeId === hardware.holeId ? hardware : h))
        : [...existing, hardware];
    return { ...piece, hardware: next };
  });
}

/** Remove the hardware seated in a given hole, if any. Used when the
 *  underlying hole is deleted so we don't leave an orphaned hardware
 *  entry pointing at a missing hole. */
export function removeHardwareForHole(
  p: Project,
  pieceId: string,
  holeId: string,
): Project {
  return updatePiece(p, pieceId, (piece) => {
    const existing = pieceHardware(piece);
    if (!existing.some((h) => h.holeId === holeId)) return piece;
    return { ...piece, hardware: existing.filter((h) => h.holeId !== holeId) };
  });
}

export function withStitchLine(
  p: Project,
  pieceId: string,
  line: StitchLine,
): Project {
  return updatePiece(p, pieceId, (piece) => {
    const existing = pieceStitchLines(piece);
    const idx = existing.findIndex((l) => l.id === line.id);
    const next =
      idx >= 0
        ? existing.map((l) => (l.id === line.id ? line : l))
        : [...existing, line];
    return { ...piece, stitchLines: next };
  });
}

/** Add or replace a project-level construction (guide) line.
 *  Replacement key is the line `id`. */
export function withConstructionLine(
  p: Project,
  line: ConstructionLine,
): Project {
  const existing = projectConstructionLines(p);
  const idx = existing.findIndex((l) => l.id === line.id);
  const next =
    idx >= 0
      ? existing.map((l) => (l.id === line.id ? line : l))
      : [...existing, line];
  return { ...p, constructionLines: next };
}

export function withDecoration(
  p: Project,
  pieceId: string,
  decoration: Decoration,
): Project {
  return updatePiece(p, pieceId, (piece) => {
    const idx = piece.decorations.findIndex((d) => d.id === decoration.id);
    const decorations =
      idx >= 0
        ? piece.decorations.map((d) =>
            d.id === decoration.id ? decoration : d,
          )
        : [...piece.decorations, decoration];
    return { ...piece, decorations };
  });
}

export function removeDecoration(
  p: Project,
  pieceId: string,
  decorationId: string,
): Project {
  return updatePiece(p, pieceId, (piece) => ({
    ...piece,
    decorations: piece.decorations.filter((d) => d.id !== decorationId),
  }));
}

export function updatePiece(
  p: Project,
  pieceId: string,
  fn: (piece: Piece) => Piece,
): Project {
  return {
    ...p,
    pieces: p.pieces.map((pc) => (pc.id === pieceId ? fn(pc) : pc)),
  };
}

export function withUserStamp(p: Project, stamp: UserStamp): Project {
  return { ...p, userStamps: [...p.userStamps, stamp] };
}

export function findPiece(p: Project, pieceId: string): Piece | undefined {
  return p.pieces.find((pc) => pc.id === pieceId);
}

export function firstPiece(p: Project): Piece | undefined {
  return p.pieces[0];
}

/** A construction group is "empty" only when it has no direct line
 *  members, no direct image members (issue #509), and no child
 *  groups. Used by the strip helpers below to decide whether to
 *  prune a group after mutation. */
function isGroupEmpty(g: ConstructionGroup): boolean {
  return (
    g.lineIds.length === 0 &&
    (g.imageIds ?? []).length === 0 &&
    (g.groupIds ?? []).length === 0
  );
}

/** Strips a set of tracing-image ids out of every existing group,
 *  dropping any group that becomes empty (issue #509). Image
 *  analogue of `stripLinesFromGroups`. The `imageIds` field is
 *  deleted when it empties so records that never had images stay
 *  byte-identical. */
function stripImagesFromGroups(
  groups: ConstructionGroup[],
  ids: ReadonlySet<string>,
): ConstructionGroup[] {
  const out: ConstructionGroup[] = [];
  let changed = false;
  for (const g of groups) {
    const existing = g.imageIds ?? [];
    const next = existing.filter((id) => !ids.has(id));
    if (next.length === existing.length) {
      out.push(g);
      continue;
    }
    changed = true;
    const candidate: ConstructionGroup = { ...g, imageIds: next };
    if (next.length === 0) {
      delete (candidate as Partial<ConstructionGroup>).imageIds;
    }
    if (!isGroupEmpty(candidate)) out.push(candidate);
  }
  return changed ? out : groups;
}

/** Strips a set of construction-line ids out of every existing
 *  construction group, dropping any group that becomes empty (both
 *  `lineIds` AND `groupIds` empty). Used as a shared subroutine by
 *  `createConstructionGroup` (so a line moves cleanly from its old
 *  group into the new one) and by `ungroupConstructionLines` (which
 *  is just "strip then keep the rest"). Returns the groups untouched
 *  when no `ids` matched, so callers can chain without an early-out. */
function stripLinesFromGroups(
  groups: ConstructionGroup[],
  ids: ReadonlySet<string>,
): ConstructionGroup[] {
  const out: ConstructionGroup[] = [];
  let changed = false;
  for (const g of groups) {
    const next = g.lineIds.filter((id) => !ids.has(id));
    if (next.length === g.lineIds.length) {
      out.push(g);
      continue;
    }
    changed = true;
    const candidate = { ...g, lineIds: next };
    if (!isGroupEmpty(candidate)) out.push(candidate);
  }
  return changed ? out : groups;
}

/** Strips a set of child-group ids out of every parent's `groupIds`,
 *  prune-on-empty just like the line-strip variant. Used when moving
 *  a nested group into a new parent. */
function stripGroupsFromParents(
  groups: ConstructionGroup[],
  ids: ReadonlySet<string>,
): ConstructionGroup[] {
  const out: ConstructionGroup[] = [];
  let changed = false;
  for (const g of groups) {
    const existing = g.groupIds ?? [];
    const next = existing.filter((id) => !ids.has(id));
    if (next.length === existing.length) {
      out.push(g);
      continue;
    }
    changed = true;
    const candidate: ConstructionGroup = { ...g, groupIds: next };
    if (next.length === 0)
      delete (candidate as Partial<ConstructionGroup>).groupIds;
    if (!isGroupEmpty(candidate)) out.push(candidate);
  }
  return changed ? out : groups;
}

/** Find the **lowest common ancestor** group of every line / group
 *  in the selection — the deepest group that already contains all
 *  selected members. Returns null when there's no common ancestor
 *  at all (some member is at top level, or the selection spans
 *  unrelated top-level subtrees).
 *
 *  For lines, the ancestor chain is `lineAncestorIds`. For selected
 *  groups, the chain is the group's `groupAncestorIds` — *not*
 *  including the group itself, since wrapping group G in a new
 *  group means G becomes a child of the new group, so the new
 *  group needs to land in G's parent, not in G. */
function commonAncestorGroupId(
  p: Project,
  lineIds: readonly string[],
  groupIds: readonly string[],
  imageIds: readonly string[] = [],
): string | null {
  if (lineIds.length === 0 && groupIds.length === 0 && imageIds.length === 0) {
    return null;
  }
  const chains: string[][] = [];
  for (const lid of lineIds) chains.push(objectRefAncestorIds(p, lineRef(lid)));
  for (const gid of groupIds)
    chains.push(objectRefAncestorIds(p, groupRef(gid)));
  for (const iid of imageIds) {
    chains.push(objectRefAncestorIds(p, imageRef(iid)));
  }
  // Walk the chains together; the LCA is the last id that matches
  // across every chain at the same depth. As soon as any chain
  // diverges (or runs out), we stop.
  let depth = 0;
  let lca: string | null = null;
  while (true) {
    const first = chains[0]![depth];
    if (first === undefined) return lca;
    for (let i = 1; i < chains.length; i++) {
      if (chains[i]![depth] !== first) return lca;
    }
    lca = first;
    depth++;
  }
}

/** Creates a new construction group containing the given lines and /
 *  or child groups. Any line / group already inside another parent
 *  is moved into the new one; parents left empty in the process are
 *  pruned. Returns the unchanged project when no targets resolve.
 *
 *  Parent-aware nesting: when every selected member shares the same
 *  direct parent group, the new group is inserted as a child of that
 *  parent (so grouping siblings reads as "add a sub-folder" rather
 *  than "yank them out to the root"). When members come from
 *  different parents — or any of them is at top level — the new
 *  group lands at the top level.
 *
 *  Membership order follows project display order so the layers
 *  panel lays things out predictably regardless of click order. */
export function createConstructionGroup(
  p: Project,
  members:
    | readonly string[]
    | {
        lineIds?: readonly string[];
        groupIds?: readonly string[];
        imageIds?: readonly string[];
      },
): { project: Project; groupId: string | null } {
  // Legacy callers pass a plain string[] of line ids; normalize the
  // input into the structured form.
  let lineIdsInput: readonly string[];
  let groupIdsInput: readonly string[];
  let imageIdsInput: readonly string[];
  if (Array.isArray(members)) {
    lineIdsInput = members;
    groupIdsInput = [];
    imageIdsInput = [];
  } else {
    const m = members as {
      lineIds?: readonly string[];
      groupIds?: readonly string[];
      imageIds?: readonly string[];
    };
    lineIdsInput = m.lineIds ?? [];
    groupIdsInput = m.groupIds ?? [];
    imageIdsInput = m.imageIds ?? [];
  }
  if (
    lineIdsInput.length === 0 &&
    groupIdsInput.length === 0 &&
    imageIdsInput.length === 0
  ) {
    return { project: p, groupId: null };
  }
  const wantedLines = new Set(lineIdsInput);
  const wantedGroups = new Set(groupIdsInput);
  const wantedImages = new Set(imageIdsInput);
  // Order all axes by their project display order.
  const orderedLines = projectConstructionLines(p)
    .map((l) => l.id)
    .filter((id) => wantedLines.has(id));
  const orderedImages = projectTracingImages(p)
    .map((img) => img.id)
    .filter((id) => wantedImages.has(id));
  // For child groups we filter against existing groups (and drop any
  // requested group that would create a cycle — i.e. is an ancestor
  // of *itself*, which can't happen, but defensive against bad input).
  const existingGroups = projectConstructionGroups(p);
  const existingIds = new Set(existingGroups.map((g) => g.id));
  const orderedGroups = existingGroups
    .map((g) => g.id)
    .filter((id) => wantedGroups.has(id) && existingIds.has(id));
  if (
    orderedLines.length === 0 &&
    orderedGroups.length === 0 &&
    orderedImages.length === 0
  ) {
    return { project: p, groupId: null };
  }
  // Decide nesting BEFORE stripping — once members are pulled out
  // of their existing parents the ancestor chains are gone. The LCA
  // is the deepest group that contains every member today; that's
  // where the new wrapper goes so the user sees "the selection just
  // got bundled inside the group it was already in" rather than
  // "the selection got hoisted to the project root."
  const lcaParentId = commonAncestorGroupId(
    p,
    orderedLines,
    orderedGroups,
    orderedImages,
  );
  // Strip the new members from any existing parents so we never end
  // up with a line / group / image occupying two parents.
  let stripped = stripLinesFromGroups(existingGroups, wantedLines);
  stripped = stripGroupsFromParents(stripped, new Set(orderedGroups));
  stripped = stripImagesFromGroups(stripped, wantedImages);
  const newGroup: ConstructionGroup = {
    id: newId("cgrp"),
    name: nextConstructionGroupName(p),
    lineIds: orderedLines,
    ...(orderedGroups.length > 0 ? { groupIds: orderedGroups } : {}),
    ...(orderedImages.length > 0 ? { imageIds: orderedImages } : {}),
  };
  const withNew = [...stripped, newGroup];
  // The LCA might have been pruned during strip if every one of its
  // members was lifted into the new group — fall back to top-level
  // so we don't dangle a child reference at a non-existent parent.
  if (lcaParentId && stripped.some((g) => g.id === lcaParentId)) {
    const withParent = withNew.map((g) =>
      g.id === lcaParentId
        ? { ...g, groupIds: [...(g.groupIds ?? []), newGroup.id] }
        : g,
    );
    return {
      project: { ...p, constructionGroups: withParent },
      groupId: newGroup.id,
    };
  }
  return {
    project: { ...p, constructionGroups: withNew },
    groupId: newGroup.id,
  };
}

/** Removes the given construction lines from their groups (if any).
 *  Lines not in any group are no-ops. Groups left empty are pruned to
 *  preserve the "no empty groups" invariant. */
export function ungroupConstructionLines(
  p: Project,
  lineIds: readonly string[],
): Project {
  if (lineIds.length === 0) return p;
  const existing = projectConstructionGroups(p);
  const next = stripLinesFromGroups(existing, new Set(lineIds));
  return next === existing ? p : { ...p, constructionGroups: next };
}

/** Removes the given tracing images from their groups (issue #509).
 *  Image analogue of `ungroupConstructionLines`: ungrouped images are
 *  no-ops, emptied groups are pruned. */
export function ungroupTracingImages(
  p: Project,
  imageIds: readonly string[],
): Project {
  if (imageIds.length === 0) return p;
  const existing = projectConstructionGroups(p);
  const next = stripImagesFromGroups(existing, new Set(imageIds));
  return next === existing ? p : { ...p, constructionGroups: next };
}

/** Pulls the given groups out of their parent groups, leaving them at
 *  the top level. Empty parents are pruned. Top-level groups in the
 *  input are no-ops. Used by Cmd+Shift+G when the selection includes
 *  nested groups. */
export function ungroupConstructionGroups(
  p: Project,
  groupIds: readonly string[],
): Project {
  if (groupIds.length === 0) return p;
  const existing = projectConstructionGroups(p);
  const next = stripGroupsFromParents(existing, new Set(groupIds));
  return next === existing ? p : { ...p, constructionGroups: next };
}

/** After a mutation that may have cascade-pruned groups (e.g., a
 *  group's only line member was moved out and the group itself got
 *  dropped), walk the remaining groups and strip references to any
 *  group id that no longer exists. Cascades: removing the dangling
 *  ref might empty another group, which then also gets pruned, so
 *  the pass repeats until stable. */
function pruneDanglingGroupRefs(
  groups: ConstructionGroup[],
): ConstructionGroup[] {
  let current = groups;
  while (true) {
    const validIds = new Set(current.map((g) => g.id));
    let changed = false;
    const next: ConstructionGroup[] = [];
    for (const g of current) {
      const existing = g.groupIds ?? [];
      const filtered = existing.filter((id) => validIds.has(id));
      if (filtered.length === existing.length) {
        next.push(g);
        continue;
      }
      changed = true;
      const candidate: ConstructionGroup = { ...g, groupIds: filtered };
      if (filtered.length === 0) {
        delete (candidate as Partial<ConstructionGroup>).groupIds;
      }
      if (!isGroupEmpty(candidate)) next.push(candidate);
    }
    if (!changed) return current;
    current = next;
  }
}

/** Re-parents construction lines and / or groups under a new owner
 *  (`targetParentGroupId === null` means top level). Each moved item
 *  is stripped from its current parent first; emptied parents are
 *  pruned. Used by the layers-panel drag-and-drop: dragging a row
 *  onto a group header re-parents into that group, dragging onto the
 *  section header re-parents to top level.
 *
 *  Filtering rules — applied in order:
 *
 *  - Targets are normalized against the current project: ids that
 *    don't exist are dropped silently.
 *  - **Cycle protection**: a moved group cannot land inside itself
 *    or one of its own descendants. Such moves are silently skipped
 *    for the offending group while still moving the rest.
 *  - **Already-direct-child**: items already directly parented to the
 *    target are dropped from the move set so the call doesn't appear
 *    as a reorder-to-end (we don't support reorder within a parent
 *    yet — separate slice).
 *  - **Inside-a-moving-group**: lines or groups whose ancestor is
 *    also in the move payload travel with the ancestor and are
 *    therefore dropped from the explicit move list. Prevents a
 *    member from being silently promoted past its enclosing group.
 *
 *  Order within the target parent: appended in project display order
 *  (`constructionLines` for lines, `constructionGroups` for groups),
 *  matching the convention `createConstructionGroup` already uses so
 *  drag-drop and group-via-shortcut layouts stay consistent. */
export function moveConstructionItems(
  p: Project,
  items: {
    lineIds?: readonly string[];
    groupIds?: readonly string[];
    imageIds?: readonly string[];
  },
  targetParentGroupId: string | null,
): Project {
  const lineIdsInput = items.lineIds ?? [];
  const groupIdsInput = items.groupIds ?? [];
  const imageIdsInput = items.imageIds ?? [];
  if (
    lineIdsInput.length === 0 &&
    groupIdsInput.length === 0 &&
    imageIdsInput.length === 0
  ) {
    return p;
  }
  const existingGroups = projectConstructionGroups(p);
  const existingLines = projectConstructionLines(p);
  const existingImages = projectTracingImages(p);
  const existingLineIds = new Set(existingLines.map((l) => l.id));
  const existingImageIds = new Set(existingImages.map((img) => img.id));
  const existingGroupIds = new Set(existingGroups.map((g) => g.id));
  if (
    targetParentGroupId !== null &&
    !existingGroupIds.has(targetParentGroupId)
  ) {
    return p;
  }
  let validLineIds = lineIdsInput.filter((id) => existingLineIds.has(id));
  let validImageIds = imageIdsInput.filter((id) => existingImageIds.has(id));
  let validGroupIds = groupIdsInput.filter((id) => existingGroupIds.has(id));
  if (targetParentGroupId !== null) {
    // A group cannot land inside itself or any of its own
    // descendants — keep the rest of the move intact.
    const targetAncestors = new Set([
      ...groupAncestorIds(p, targetParentGroupId),
      targetParentGroupId,
    ]);
    validGroupIds = validGroupIds.filter((gid) => !targetAncestors.has(gid));
  }
  // Inside-a-moving-group filter: a line/image whose parent group is
  // being moved travels with the group; ditto a group whose ancestor
  // group is being moved. Excluding them from the explicit list keeps
  // the hierarchy intact rather than promoting children past their
  // moving parent.
  const movingGroupSet = new Set(validGroupIds);
  if (movingGroupSet.size > 0) {
    const movedLineDescendants = new Set<string>();
    const movedImageDescendants = new Set<string>();
    for (const gid of validGroupIds) {
      const members = descendantLeafIds(p, gid);
      for (const lid of members.lineIds) movedLineDescendants.add(lid);
      for (const iid of members.imageIds) movedImageDescendants.add(iid);
    }
    validLineIds = validLineIds.filter((id) => !movedLineDescendants.has(id));
    validImageIds = validImageIds.filter(
      (id) => !movedImageDescendants.has(id),
    );
    validGroupIds = validGroupIds.filter((gid) => {
      const ancestors = groupAncestorIds(p, gid);
      return !ancestors.some((aid) => movingGroupSet.has(aid));
    });
  }
  // Already-direct-child filter: lines / groups / images already in
  // the target parent's direct children list aren't touched (would
  // otherwise reorder-to-end since strip+append puts them at the back).
  const targetGroup =
    targetParentGroupId !== null
      ? existingGroups.find((g) => g.id === targetParentGroupId)
      : null;
  const targetLineSet = new Set(targetGroup?.lineIds ?? []);
  const targetGroupSet = new Set(targetGroup?.groupIds ?? []);
  const targetImageSet = new Set(targetGroup?.imageIds ?? []);
  const childGroupIds = new Set<string>();
  for (const g of existingGroups) {
    for (const id of g.groupIds ?? []) childGroupIds.add(id);
  }
  const groupedLineIds = new Set<string>();
  const groupedImageIds = new Set<string>();
  for (const g of existingGroups) {
    for (const id of g.lineIds) groupedLineIds.add(id);
    for (const id of g.imageIds ?? []) groupedImageIds.add(id);
  }
  validLineIds = validLineIds.filter((id) =>
    targetParentGroupId === null
      ? groupedLineIds.has(id)
      : !targetLineSet.has(id),
  );
  validImageIds = validImageIds.filter((id) =>
    targetParentGroupId === null
      ? groupedImageIds.has(id)
      : !targetImageSet.has(id),
  );
  validGroupIds = validGroupIds.filter((gid) =>
    targetParentGroupId === null
      ? childGroupIds.has(gid)
      : !targetGroupSet.has(gid),
  );
  if (
    validLineIds.length === 0 &&
    validGroupIds.length === 0 &&
    validImageIds.length === 0
  ) {
    return p;
  }
  // Order by project display order so the panel reads consistently
  // regardless of selection-click order.
  const lineSet = new Set(validLineIds);
  const groupSet = new Set(validGroupIds);
  const imageSet = new Set(validImageIds);
  const orderedLines = existingLines
    .map((l) => l.id)
    .filter((id) => lineSet.has(id));
  const orderedGroups = existingGroups
    .map((g) => g.id)
    .filter((id) => groupSet.has(id));
  const orderedImages = existingImages
    .map((img) => img.id)
    .filter((id) => imageSet.has(id));
  let stripped = stripLinesFromGroups(existingGroups, new Set(orderedLines));
  stripped = stripGroupsFromParents(stripped, new Set(orderedGroups));
  stripped = stripImagesFromGroups(stripped, new Set(orderedImages));
  // Cleanup: stripLinesFromGroups can cascade-prune a single-member
  // group whose only line was moved away, but its parent still
  // references the now-vanished id. Sweep those refs before adding
  // back to the target.
  stripped = pruneDanglingGroupRefs(stripped);
  if (targetParentGroupId === null) {
    return { ...p, constructionGroups: stripped };
  }
  // The target itself may have been cascade-pruned during cleanup
  // (e.g., the user dragged the last line out of a leaf group while
  // simultaneously targeting that group — a no-op the
  // already-direct-child filter prevents in practice, but defensive
  // for unusual call sites). Fall back to top-level placement.
  if (!stripped.some((g) => g.id === targetParentGroupId)) {
    return { ...p, constructionGroups: stripped };
  }
  const next = stripped.map((g) => {
    if (g.id !== targetParentGroupId) return g;
    const existingChildGroups = g.groupIds ?? [];
    const mergedGroupIds = [...existingChildGroups, ...orderedGroups];
    const mergedImageIds = [...(g.imageIds ?? []), ...orderedImages];
    const candidate: ConstructionGroup = {
      ...g,
      lineIds: [...g.lineIds, ...orderedLines],
    };
    if (mergedGroupIds.length > 0) candidate.groupIds = mergedGroupIds;
    if (mergedImageIds.length > 0) candidate.imageIds = mergedImageIds;
    return candidate;
  });
  return { ...p, constructionGroups: next };
}

/** Position-aware move/reorder for construction lines + groups. Same
 *  contract as `moveConstructionItems` for re-parenting, but with an
 *  `insertBefore` cursor that controls where in the target parent the
 *  moving items land. `insertBefore: null` means append (identical to
 *  `moveConstructionItems`). When `insertBefore` names a specific
 *  sibling in the target, moving items of the cursor's *kind* (line
 *  or group) are spliced into the matching array immediately before
 *  the cursor; moving items of the opposite kind append to their
 *  array (mixed-kind drops with a single cursor have no unambiguous
 *  position, so the cursor's kind wins).
 *
 *  Top-level reorder works on `project.constructionLines[]` /
 *  `project.constructionGroups[]` — the moving records are spliced in
 *  before the cursor record, leaving non-moving records in place. The
 *  layers panel renders top-level groups from `constructionGroups`
 *  filtered to top-level and ungrouped lines from `constructionLines`
 *  filtered to ungrouped, so the splice is enough to reposition the
 *  visible row without disturbing nested-group / grouped-line
 *  records around it.
 *
 *  Fallbacks: an invalid cursor (cursor not a direct child of target,
 *  or cursor is itself in the moving set) degrades to append. Cycle
 *  prevention, parent-of-moving filtering, and id validation match
 *  `moveConstructionItems` exactly. */
export function moveConstructionItemsAt(
  p: Project,
  items: {
    lineIds?: readonly string[];
    groupIds?: readonly string[];
    imageIds?: readonly string[];
  },
  targetParentGroupId: string | null,
  insertBefore: { kind: "line" | "group" | "image"; id: string } | null,
): Project {
  // Note: do *not* delegate to `moveConstructionItems` when
  // `insertBefore` is null — that function has an "already-direct-
  // child" filter that turns a same-parent reorder-to-end into a
  // no-op (the dropped line is "already in this group, skip"). Here
  // we want the drop on the trailing end strip to actually reorder
  // to the end, so the null case falls through to the same
  // strip-and-append logic below.
  const lineIdsInput = items.lineIds ?? [];
  const groupIdsInput = items.groupIds ?? [];
  const imageIdsInput = items.imageIds ?? [];
  if (
    lineIdsInput.length === 0 &&
    groupIdsInput.length === 0 &&
    imageIdsInput.length === 0
  ) {
    return p;
  }
  const existingGroups = projectConstructionGroups(p);
  const existingLines = projectConstructionLines(p);
  const existingImages = projectTracingImages(p);
  const existingLineIds = new Set(existingLines.map((l) => l.id));
  const existingImageIds = new Set(existingImages.map((img) => img.id));
  const existingGroupIds = new Set(existingGroups.map((g) => g.id));
  if (
    targetParentGroupId !== null &&
    !existingGroupIds.has(targetParentGroupId)
  ) {
    return p;
  }
  let validLineIds = lineIdsInput.filter((id) => existingLineIds.has(id));
  let validImageIds = imageIdsInput.filter((id) => existingImageIds.has(id));
  let validGroupIds = groupIdsInput.filter((id) => existingGroupIds.has(id));
  if (targetParentGroupId !== null) {
    const targetAncestors = new Set([
      ...groupAncestorIds(p, targetParentGroupId),
      targetParentGroupId,
    ]);
    validGroupIds = validGroupIds.filter((gid) => !targetAncestors.has(gid));
  }
  // Inside-a-moving-group filter mirrors moveConstructionItems: a
  // line/image/group whose ancestor group is moving rides along
  // inside the ancestor — listing it explicitly would promote it
  // past the parent.
  const movingGroupSet = new Set(validGroupIds);
  if (movingGroupSet.size > 0) {
    const movedLineDescendants = new Set<string>();
    const movedImageDescendants = new Set<string>();
    for (const gid of validGroupIds) {
      const members = descendantLeafIds(p, gid);
      for (const lid of members.lineIds) movedLineDescendants.add(lid);
      for (const iid of members.imageIds) movedImageDescendants.add(iid);
    }
    validLineIds = validLineIds.filter((id) => !movedLineDescendants.has(id));
    validImageIds = validImageIds.filter(
      (id) => !movedImageDescendants.has(id),
    );
    validGroupIds = validGroupIds.filter((gid) => {
      const ancestors = groupAncestorIds(p, gid);
      return !ancestors.some((aid) => movingGroupSet.has(aid));
    });
  }
  // No already-direct-child filter here — that's exactly the reorder
  // case (item is already in target, just at the wrong position).
  // Cursor invariants: when non-null, must not be in moving set and
  // must be a direct child of the target (otherwise the splice has
  // no anchor). A self-anchored cursor degrades to a no-op (callers
  // should already be filtering this in the UI; defensive).
  if (insertBefore !== null) {
    const movingLineSet = new Set(validLineIds);
    const movingGroupSet2 = new Set(validGroupIds);
    const movingImageSet = new Set(validImageIds);
    const cursorInMovingSet =
      (insertBefore.kind === "line" && movingLineSet.has(insertBefore.id)) ||
      (insertBefore.kind === "group" && movingGroupSet2.has(insertBefore.id)) ||
      (insertBefore.kind === "image" && movingImageSet.has(insertBefore.id));
    if (cursorInMovingSet) return p;
  }
  if (
    validLineIds.length === 0 &&
    validGroupIds.length === 0 &&
    validImageIds.length === 0
  ) {
    return p;
  }
  const lineSet = new Set(validLineIds);
  const groupSet = new Set(validGroupIds);
  const imageSet = new Set(validImageIds);
  const orderedLines = existingLines
    .map((l) => l.id)
    .filter((id) => lineSet.has(id));
  const orderedGroups = existingGroups
    .map((g) => g.id)
    .filter((id) => groupSet.has(id));
  const orderedImages = existingImages
    .map((img) => img.id)
    .filter((id) => imageSet.has(id));
  let stripped = stripLinesFromGroups(existingGroups, new Set(orderedLines));
  stripped = stripGroupsFromParents(stripped, new Set(orderedGroups));
  stripped = stripImagesFromGroups(stripped, new Set(orderedImages));
  stripped = pruneDanglingGroupRefs(stripped);

  if (targetParentGroupId === null) {
    // Top-level placement: splice moving records into
    // constructionLines[]/constructionGroups[] at the cursor's
    // current position so the panel reads as expected. Null cursor
    // moves moving records to the end of their respective array
    // (the trailing end-strip semantic) so reorder-to-end works for
    // items that are already top-level without needing the duplicate
    // filter that `moveConstructionItems` uses.
    let nextLines = existingLines;
    let nextGroups = stripped;
    let nextImages = existingImages;
    if (insertBefore === null || insertBefore.kind === "line") {
      if (orderedLines.length > 0) {
        const without = nextLines.filter((l) => !lineSet.has(l.id));
        const moving = nextLines.filter((l) => lineSet.has(l.id));
        if (insertBefore === null || insertBefore.kind !== "line") {
          nextLines = [...without, ...moving];
        } else {
          const adj = without.findIndex((l) => l.id === insertBefore.id);
          if (adj < 0) {
            nextLines = [...without, ...moving];
          } else {
            nextLines = [
              ...without.slice(0, adj),
              ...moving,
              ...without.slice(adj),
            ];
          }
        }
      }
    }
    if (insertBefore === null || insertBefore.kind === "group") {
      if (orderedGroups.length > 0) {
        const without = nextGroups.filter((g) => !groupSet.has(g.id));
        const moving = nextGroups.filter((g) => groupSet.has(g.id));
        if (insertBefore === null || insertBefore.kind !== "group") {
          nextGroups = [...without, ...moving];
        } else {
          const adj = without.findIndex((g) => g.id === insertBefore.id);
          if (adj < 0) {
            nextGroups = [...without, ...moving];
          } else {
            nextGroups = [
              ...without.slice(0, adj),
              ...moving,
              ...without.slice(adj),
            ];
          }
        }
      }
    }
    // Ungrouped images live in `tracingImages[]`; reorder them there
    // (issue #509) the same way ungrouped lines reorder in
    // `constructionLines[]`.
    if (insertBefore === null || insertBefore.kind === "image") {
      if (orderedImages.length > 0) {
        const without = nextImages.filter((img) => !imageSet.has(img.id));
        const moving = nextImages.filter((img) => imageSet.has(img.id));
        if (insertBefore === null || insertBefore.kind !== "image") {
          nextImages = [...without, ...moving];
        } else {
          const adj = without.findIndex((img) => img.id === insertBefore.id);
          if (adj < 0) {
            nextImages = [...without, ...moving];
          } else {
            nextImages = [
              ...without.slice(0, adj),
              ...moving,
              ...without.slice(adj),
            ];
          }
        }
      }
    }
    // #887: maintain the single interleaved top-level ordering so a
    // dropped loose line / group lands exactly at the cursor —
    // including a loose line above / below / between groups, which the
    // separate `constructionLines[]` / `constructionGroups[]` arrays
    // above cannot express. Images are not part of this sequence (they
    // render in the decoration section), so an image-only reorder
    // leaves the order untouched.
    let nextTopLevelOrder = p.constructionTopLevelOrder;
    if (orderedLines.length > 0 || orderedGroups.length > 0) {
      const movingKeys = new Set<string>([
        ...orderedGroups.map((id) => `group:${id}`),
        ...orderedLines.map((id) => `line:${id}`),
      ]);
      // Resolve the pre-move interleaved order, drop the moving entries,
      // then splice them back in before the cursor (append when the
      // cursor is null or points at the opposite kind / a missing anchor).
      const kept = topLevelConstructionOrder(p).filter(
        (ref) => !movingKeys.has(`${ref.kind}:${ref.id}`),
      );
      const movingRefs: TopLevelConstructionRef[] = [
        ...orderedGroups.map((id) => ({ kind: "group" as const, id })),
        ...orderedLines.map((id) => ({ kind: "line" as const, id })),
      ];
      let insertIdx = kept.length;
      if (
        insertBefore !== null &&
        (insertBefore.kind === "line" || insertBefore.kind === "group")
      ) {
        const at = kept.findIndex(
          (ref) => ref.kind === insertBefore.kind && ref.id === insertBefore.id,
        );
        if (at >= 0) insertIdx = at;
      }
      nextTopLevelOrder = [
        ...kept.slice(0, insertIdx),
        ...movingRefs,
        ...kept.slice(insertIdx),
      ];
    }
    return {
      ...p,
      constructionLines: nextLines,
      constructionGroups: nextGroups,
      tracingImages: nextImages,
      ...(nextTopLevelOrder !== undefined
        ? { constructionTopLevelOrder: nextTopLevelOrder }
        : {}),
    };
  }

  // Inside-a-group target: insert at the cursor's position in the
  // matching sibling array. Null cursor (= trailing end strip)
  // appends both kinds to the end. Cursor missing from target (e.g.
  // cursor item lives in a different parent) falls back to append
  // for that kind.
  if (!stripped.some((g) => g.id === targetParentGroupId)) {
    return { ...p, constructionGroups: stripped };
  }
  const next = stripped.map((g) => {
    if (g.id !== targetParentGroupId) return g;
    const existingChildLines = g.lineIds.filter((id) => !lineSet.has(id));
    const existingChildGroups = (g.groupIds ?? []).filter(
      (id) => !groupSet.has(id),
    );
    const existingChildImages = (g.imageIds ?? []).filter(
      (id) => !imageSet.has(id),
    );
    let nextLineIds = [...existingChildLines, ...orderedLines];
    let nextGroupIds = [...existingChildGroups, ...orderedGroups];
    let nextImageIds = [...existingChildImages, ...orderedImages];
    // Splice the moving items of the cursor's kind in before the
    // cursor; the other kinds append (a single cursor can only place
    // one kind unambiguously).
    if (insertBefore !== null && insertBefore.kind === "line") {
      const cursorIdx = existingChildLines.indexOf(insertBefore.id);
      if (cursorIdx >= 0) {
        nextLineIds = [
          ...existingChildLines.slice(0, cursorIdx),
          ...orderedLines,
          ...existingChildLines.slice(cursorIdx),
        ];
      }
    } else if (insertBefore !== null && insertBefore.kind === "group") {
      const cursorIdx = existingChildGroups.indexOf(insertBefore.id);
      if (cursorIdx >= 0) {
        nextGroupIds = [
          ...existingChildGroups.slice(0, cursorIdx),
          ...orderedGroups,
          ...existingChildGroups.slice(cursorIdx),
        ];
      }
    } else if (insertBefore !== null && insertBefore.kind === "image") {
      const cursorIdx = existingChildImages.indexOf(insertBefore.id);
      if (cursorIdx >= 0) {
        nextImageIds = [
          ...existingChildImages.slice(0, cursorIdx),
          ...orderedImages,
          ...existingChildImages.slice(cursorIdx),
        ];
      }
    }
    const candidate: ConstructionGroup = { ...g, lineIds: nextLineIds };
    if (nextGroupIds.length > 0) candidate.groupIds = nextGroupIds;
    else delete candidate.groupIds;
    if (nextImageIds.length > 0) candidate.imageIds = nextImageIds;
    else delete candidate.imageIds;
    return candidate;
  });
  return { ...p, constructionGroups: next };
}

/** Dissolves a single group: any direct line members revert to the
 *  ungrouped pool, and any child groups are promoted to the dissolved
 *  group's former parent (or top-level if it was top-level). Used by
 *  the layers-panel "Keep" branch on the group × prompt — the lines
 *  shouldn't disappear, but the group record itself should. The
 *  parent-promote keeps nested-group hierarchies coherent: dissolving
 *  a middle level lifts the grandchildren one rung up rather than
 *  orphaning them.
 *
 *  Note: this function intentionally mutates *structure* only; line
 *  records and their constraint refs are untouched. */
function dissolveOneGroup(
  groups: ConstructionGroup[],
  groupId: string,
): ConstructionGroup[] {
  const target = groups.find((g) => g.id === groupId);
  if (!target) return groups;
  const childGroupIds = target.groupIds ?? [];
  // Find the parent (if any) and splice the dissolved group's
  // children into the parent's groupIds slot where the dissolved
  // group itself was.
  let parentReplaced = false;
  const next: ConstructionGroup[] = [];
  for (const g of groups) {
    if (g.id === groupId) continue; // Dissolved.
    const gKids = g.groupIds ?? [];
    if (gKids.includes(groupId)) {
      parentReplaced = true;
      const idx = gKids.indexOf(groupId);
      const merged = [
        ...gKids.slice(0, idx),
        ...childGroupIds,
        ...gKids.slice(idx + 1),
      ];
      const candidate: ConstructionGroup = { ...g, groupIds: merged };
      if (merged.length === 0)
        delete (candidate as Partial<ConstructionGroup>).groupIds;
      if (!isGroupEmpty(candidate)) next.push(candidate);
      continue;
    }
    next.push(g);
  }
  void parentReplaced;
  return next;
}

/** Renames a construction group. Trimmed empty names fall back to the
 *  auto "Group N" pattern so the panel never renders a row with a
 *  blank label after an accidental clear-and-commit. */
export function renameConstructionGroup(
  p: Project,
  groupId: string,
  name: string,
): Project {
  const trimmed = name.trim();
  const fallback = nextConstructionGroupName(p);
  return {
    ...p,
    constructionGroups: projectConstructionGroups(p).map((g) =>
      g.id === groupId
        ? { ...g, name: trimmed.length > 0 ? trimmed : fallback }
        : g,
    ),
  };
}

/** Drops a construction group without touching its member lines —
 *  direct line members revert to ungrouped, and any nested child
 *  groups are promoted into the dissolved group's former parent (or
 *  to top-level if the dissolved group was already top-level). Used
 *  by the "Keep" branch of the layers-panel × prompt. */
export function dissolveConstructionGroup(
  p: Project,
  groupId: string,
): Project {
  const groups = projectConstructionGroups(p);
  if (!groups.some((g) => g.id === groupId)) return p;
  const next = dissolveOneGroup(groups, groupId);
  return { ...p, constructionGroups: next };
}

/**
 * After deleting a decoration from a piece, returns the id of the decoration
 * that should take selection — the one that was after it in the list, or the
 * one before if it was the last. Returns null if the piece has no other
 * decorations (caller should fall back to selecting the parent part).
 */
export function pickAdjacentDecoration(
  piece: Piece,
  deletedId: string,
): string | null {
  const idx = piece.decorations.findIndex((d) => d.id === deletedId);
  if (idx < 0) return null;
  return (
    piece.decorations[idx + 1]?.id ?? piece.decorations[idx - 1]?.id ?? null
  );
}

/** When the Slice / Freehand-cut tool cuts a construction line
 *  with offset descendants (issue #275), the source line is
 *  mutated in place (first half keeps the original id) and one or
 *  more tail pieces get fresh ids. The first half's offset subtree
 *  re-derives automatically — every offset child still references
 *  the original id, and `deriveConstructionOffsets` picks up the
 *  new pathData on the next commit. But each tail piece has no
 *  offset children, so the "tail half" of the offset stack would
 *  silently vanish.
 *
 *  This helper deep-clones the entire offset subtree rooted at
 *  `originalSourceId` and re-roots the clones at `tailSourceId`,
 *  remapping every `offsetSource.sourceId` down the chain so an
 *  offset-of-offset stack is preserved on the tail. All new lines
 *  get fresh ids and `"<source> (offset)"` names via
 *  `nextConstructionOffsetName`, naming against the project view
 *  that includes every previously-created clone so names stay
 *  unique across a batch.
 *
 *  `baseProject` MUST already include the tail line (the immediate
 *  parent of the first generation of clones) so the offset-name
 *  helper can read its name. Returns the new lines in derivation
 *  order (parent before child) — the caller appends them to the
 *  next project state. */
export function duplicateOffsetSubtreeForSlice(
  baseProject: Project,
  originalSourceId: string,
  tailSourceId: string,
): ConstructionLine[] {
  const allLines = projectConstructionLines(baseProject);
  const childrenBySource = new Map<string, ConstructionLine[]>();
  for (const l of allLines) {
    if (!l.offsetSource) continue;
    const arr = childrenBySource.get(l.offsetSource.sourceId);
    if (arr) arr.push(l);
    else childrenBySource.set(l.offsetSource.sourceId, [l]);
  }
  const created: ConstructionLine[] = [];
  let projForNaming = baseProject;
  const walk = (origParentId: string, newParentId: string) => {
    const children = childrenBySource.get(origParentId) ?? [];
    for (const child of children) {
      if (!child.offsetSource) continue;
      const newId_ = newId("con");
      const newParent = projectConstructionLines(projForNaming).find(
        (l) => l.id === newParentId,
      );
      const newName = newParent
        ? nextConstructionOffsetName(projForNaming, newParent)
        : child.name;
      const newChild: ConstructionLine = {
        ...child,
        id: newId_,
        name: newName,
        offsetSource: { ...child.offsetSource, sourceId: newParentId },
      };
      created.push(newChild);
      projForNaming = {
        ...projForNaming,
        constructionLines: [
          ...projectConstructionLines(projForNaming),
          newChild,
        ],
      };
      walk(child.id, newChild.id);
    }
  };
  walk(originalSourceId, tailSourceId);
  return created;
}
